// src/pages/Register.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaEnvelope,
  FaLock,
  FaCheckCircle,
  FaTimesCircle,
  FaTag,
  FaLock as FaLockIcon,
  FaInfoCircle,
  FaPhone,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { toast } from 'react-toastify';
import WeChatLoginButton from '../components/WeChatLoginButton'; // WeChat ログインボタンを読み込み

// クーポンコード形式（A–Z / 0–9 / -、4〜32文字）の簡易バリデーション用
const CODE_RE = /^[A-Z0-9-]{4,32}$/;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // --- URL パラメータ（リダイレクト先・クーポン・招待など） ---
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo') || '/mypage';
  const couponFromUrl = params.get('coupon'); // 割引リンク経由のクーポン（大文字想定）
  const linkCodeFromUrl = params.get('lc');   // クーポンリンクのトラッキング用コード
  const referralFromUrl = params.get('r') || params.get('ref'); // 招待リンク（紹介プログラム用）

  // --- フォーム状態 ---
  const [registrationMode, setRegistrationMode] = useState<'email' | 'phone'>('email');

  // Fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // Keep for compat
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');

  const p1Ref = useRef<HTMLInputElement>(null);
  const p2Ref = useRef<HTMLInputElement>(null);
  const p3Ref = useRef<HTMLInputElement>(null);

  const [verificationCode, setVerificationCode] = useState('');

  // --- SMS 認証コード関連の状態 ---
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // --- クーポンコード（割引リンクからの場合はロック） ---
  const [coupon, setCoupon] = useState('');
  const [couponLocked, setCouponLocked] = useState(false);

  // --- 招待リンクのバナー表示フラグ ---
  const [showReferralBanner, setShowReferralBanner] = useState(false);

  // --- UI 状態（エラー / 成功 / ローディング / ボタンシェイク） ---
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shakeBtn, setShakeBtn] = useState(false);

  // --- パスワード強度チェック用のルール ---
  const rules = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  const isPasswordValid = Object.values(rules).every(Boolean);
  const strengthLevel = Object.values(rules).filter(Boolean).length;

  // パスワード強度メーターの色を返すヘルパー
  const getStrengthColor = () => {
    switch (strengthLevel) {
      case 0:
      case 1:
        return 'bg-red-400';
      case 2:
        return 'bg-yellow-400';
      case 3:
        return 'bg-coral/80';
      case 4:
      case 5:
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  // パスワード強度ラベルを返すヘルパー
  const getStrengthLabel = () => {
    switch (strengthLevel) {
      case 0:
      case 1:
        return t('auth.strength.weak') || '弱い';
      case 2:
        return t('auth.strength.fair') || 'やや弱い';
      case 3:
        return t('auth.strength.good') || '良い';
      case 4:
      case 5:
        return t('auth.strength.strong') || '強い';
      default:
        return '';
    }
  };

  // --- 初期化: URL パラメータに基づくクーポンと招待バナーの制御 ---
  useEffect(() => {
    if (couponFromUrl) {
      // 割引リンク経由：クーポンコードを固定してロック
      setCoupon(couponFromUrl.toUpperCase());
      setCouponLocked(true);
      setShowReferralBanner(false);
    } else if (referralFromUrl) {
      // 招待リンク経由：クーポンは空＆編集可能、招待バナーのみ表示
      setCoupon('');
      setCouponLocked(false);
      setShowReferralBanner(true);
    } else {
      setCouponLocked(false);
      setShowReferralBanner(false);
    }
  }, [couponFromUrl, referralFromUrl]);

  // ボタンのシェイクアニメーションを発火させるヘルパー
  const triggerShake = () => {
    setShakeBtn(true);
    window.setTimeout(() => setShakeBtn(false), 500);
  };

  // エラーメッセージを設定しつつ、ボタンをシェイクさせるヘルパー
  const setAndShakeError = (msg: string) => {
    setError(msg);
    triggerShake();
  };

  // クーポン入力値の更新（ロックされている場合は変更不可）
  const handleCouponChange = (v: string) => {
    const up = v.toUpperCase();
    setCoupon(up);
  };

  // クーポンの軽いバリデーション（手入力時のみ）
  const couponError = React.useMemo(() => {
    if (!couponLocked && coupon.trim()) {
      if (!CODE_RE.test(coupon.trim().toUpperCase())) {
        return t('auth.coupon_format_hint') || 'A–Z・0–9・-（4〜32文字）で入力してください';
      }
    }
    return null;
  }, [coupon, couponLocked, t]);

  // --- SMS 再送用カウントダウンの管理 ---
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // SMS 認証コード送信処理
  const handleSendCode = async () => {
    const fullPhoneNumber = `+81${phone1}${phone2}${phone3}`;
    const pNum = `${phone1}${phone2}${phone3}`;

    if (pNum.length < 10) {
      setError(t('auth.error_phone_invalid') || 'Invalid phone number');
      return;
    }

    setSendingCode(true);
    setError(null);
    try {
      await api.post('/auth/sms/request', { phone_number: fullPhoneNumber });
      setCodeSent(true);
      setCountdown(60); // 60秒間再送不可
    } catch (err: any) {
      const data = err?.response?.data;
      const code = data?.code;

      // Handle rate limit error with i18n
      if (code === 'RATE_LIMIT_EXCEEDED' && data?.seconds) {
        setAndShakeError(t('auth.rate_limit_exceeded', { seconds: data.seconds }));
      } else {
        const errorMsg = data?.detail || data?.message || t('auth.error_network');
        setAndShakeError(errorMsg);
      }
    } finally {
      setSendingCode(false);
    }
  };

  // --- 登録フォーム送信処理（メール / 電話共通） ---
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);

    // 登録モード別のバリデーション
    if (registrationMode === 'email') {
      const emailTrimmed = email.trim();

      if (!emailTrimmed && !password) {
        setAndShakeError(t('auth.error_empty') || 'メールアドレスとパスワードを入力してください');
        return;
      }
      if (!emailTrimmed) {
        setAndShakeError(t('auth.error_email_required') || 'メールアドレスを入力してください');
        return;
      }
      if (!password) {
        setAndShakeError(t('auth.error_password_required') || 'パスワードを入力してください');
        return;
      }
      if (password !== confirmPassword) {
        setAndShakeError(t('auth.error_password_mismatch') || 'パスワードが一致しません');
        return;
      }

      // パスワードポリシーを満たしているか確認
      if (!isPasswordValid) {
        setAndShakeError(t('auth.error_password_policy') || 'パスワード条件を満たしていません');
        return;
      }

      // 手入力クーポンがある場合の軽い形式チェック
      if (!couponLocked && coupon.trim() && !CODE_RE.test(coupon.trim().toUpperCase())) {
        setAndShakeError(t('auth.coupon_format_hint') || 'クーポン形式が正しくありません');
        return;
      }
    } else {
      // 電話登録モードのバリデーション
      if ((!phone1 || !phone2 || !phone3) || !password || !confirmPassword || !verificationCode) {
        setError(t('auth.error_fill_all') || 'Please fill in all fields');
        return;
      }
      if (password !== confirmPassword) {
        setAndShakeError(t('auth.error_password_mismatch') || 'パスワードが一致しません');
        return;
      }
      if (!isPasswordValid) {
        setAndShakeError(t('auth.error_password_policy') || 'パスワード条件を満たしていません');
        return;
      }
    }

    setLoading(true);
    try {
      // 実際に有効なクーポンコードだけを sessionStorage に一時保存しておく
      if (coupon.trim()) {
        sessionStorage.setItem('pending_coupon_code', coupon.trim().toUpperCase());
      } else {
        sessionStorage.removeItem('pending_coupon_code');
      }
      sessionStorage.removeItem('pending_coupon_link_code');

      // --- 送信ペイロードの組み立て ---
      const payload: any = {
        password: password.trim(),
        provider: registrationMode, // 'email' または 'phone'
      };

      if (registrationMode === 'email') {
        payload.email = email.trim();
        payload.coupon = coupon.trim() ? coupon.trim().toUpperCase() : undefined;
      } else {
        payload.phone_number = `+81${phone1}${phone2}${phone3}`;
        payload.verification_code = verificationCode.trim();
      }

      // 割引リンク用の link code を meta 的に付与したい場合
      if (linkCodeFromUrl) {
        payload.lc = linkCodeFromUrl;
      }

      // --- axios 用設定（紹介コード ref をクエリやヘッダーに付与） ---
      const config: any = {
        headers: {
          'Content-Type': 'application/json',
        },
        // 招待コードをクエリパラメータとして付与
        params: referralFromUrl ? { ref: referralFromUrl } : undefined,
      };

      // ヘッダーにも X-Referral として追加（バックエンド側で利用可能）
      if (referralFromUrl) {
        config.headers['X-Referral'] = referralFromUrl;
      }

      // 登録モード別にエンドポイントを切り替え
      const endpoint =
        registrationMode === 'email'
          ? '/auth/register/email' // メール登録（メール検証フロー）
          : '/auth/register'; // 電話登録（SMS 検証フロー）

      const res = await api.post(endpoint, payload, config);

      if (res && res.status >= 200 && res.status < 300) {
        setSuccess(true);

        // 電話登録で auto_login が返ってきた場合は、そのまま自動ログイン
        if (registrationMode === 'phone' && res.data?.auto_login && res.data?.access_token) {
          localStorage.setItem('token', res.data.access_token);
          localStorage.setItem('role', res.data.role || 'user');

          setTimeout(() => {
            toast.success(t('auth.register_success') || '注册成功！正在跳转...');
            navigate(redirectTo);
          }, 500);
          return;
        }

        // メール登録の場合は認証メール送信後、メール確認待ち画面へリダイレクト
        setTimeout(() => {
          if (registrationMode === 'email') {
            navigate(
              '/verify-pending?email=' +
              encodeURIComponent(email.trim()) +
              '&redirectTo=' +
              encodeURIComponent(redirectTo),
            );
          } else {
            // 電話登録で自動ログインしないケース → 成功後そのままマイページなどへ
            toast.success(t('auth.register_success') || '注册成功！');
            navigate(redirectTo);
          }
        }, 800);
      } else {
        setAndShakeError(t('auth.error_failed') || '登録に失敗しました');
      }
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 409) {
        // 409 = メールアドレス重複
        setAndShakeError(
          t('auth.email_already_registered') || 'このメールアドレスは既に登録されています',
        );
      } else {
        const data = err?.response?.data;
        const msg =
          data?.message ||
          data?.detail ||
          (typeof data === 'string' ? data : '') ||
          (t('auth.error_network') as string) ||
          'ネットワークエラー';
        setAndShakeError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // パスワードルールの表示用コンポーネント
  const renderRule = (passed: boolean, label: string) => (
    <div className={`flex items-center gap-2 text-sm ${passed ? 'text-green-600' : 'text-gray-500'}`}>
      {passed ? <FaCheckCircle /> : <FaTimesCircle />}
      {label}
    </div>
  );

  const handlePhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLen: number,
    nextRef: React.RefObject<HTMLInputElement | null> | null
  ) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= maxLen) {
      setter(val);
      if (val.length === maxLen && nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    let digits = pasted.replace(/\D/g, '');

    if (digits.startsWith('81') && digits.length > 10) {
      digits = digits.substring(2);
    }

    if (digits.length >= 10) {
      let p1 = '', p2 = '', p3 = '';
      if (digits.length === 11) {
        p1 = digits.slice(0, 3);
        p2 = digits.slice(3, 7);
        p3 = digits.slice(7);
      } else {
        if (digits.startsWith('03') || digits.startsWith('06')) {
          p1 = digits.slice(0, 2);
          p2 = digits.slice(2, 6);
          p3 = digits.slice(6);
        } else {
          p1 = digits.slice(0, 3);
          p2 = digits.slice(3, 7);
          p3 = digits.slice(7);
        }
      }
      setPhone1(p1);
      setPhone2(p2);
      setPhone3(p3);
      setTimeout(() => p3Ref.current?.focus(), 0);
    } else {
      setPhone1(digits.slice(0, 3));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 font-sans px-4">
      {/* ボタンのシェイクアニメーション用 keyframes 定義 */}
      <style>
        {`
          @keyframes btn-shake {
            10%, 90% { transform: translateX(-1px); }
            20%, 80% { transform: translateX(2px); }
            30%, 50%, 70% { transform: translateX(-4px); }
            40%, 60% { transform: translateX(4px); }
          }
          .animate-shake { animation: btn-shake 0.45s ease-in-out both; }
        `}
      </style>

      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center text-coral">
          {t('auth.register_title') || '新規登録'}
        </h2>

        {/* 招待リンク経由かつクーポン無しの場合に表示するバナー */}
        {showReferralBanner && !couponFromUrl && (
          <div className="mb-4 rounded-lg border border-coral/20 bg-coral/5 px-3 py-2 text-sm text-navy flex items-start gap-2">
            <FaInfoCircle className="mt-0.5" />
            <div>
              <div className="font-medium">
                {t('referral.banner.title') || '招待リンクからアクセスしました'}
              </div>
              <div>
                {t('referral.banner.note') ||
                  '招待を記録しました（割引は適用されません）。特典はマイページでご確認ください。'}
              </div>
            </div>
          </div>
        )}

        {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
        {success && (
          <p className="text-green-600 mb-4 text-center">
            {t('auth.verify_mail_sent') ||
              '確認メールを送信しました。メール内のリンクから認証を完了してください。'}
          </p>
        )}

        {/* メール / 電話登録のタブ切り替え */}
        <div className="flex mb-6 border-b border-gray-200">
          <button
            type="button"
            className={`flex-1 py-2 text-center font-medium transition ${registrationMode === 'email'
              ? 'text-coral border-b-2 border-coral'
              : 'text-gray-500 hover:text-gray-700'
              }`}
            onClick={() => {
              setRegistrationMode('email');
              setError(null);
            }}
          >
            {t('auth.email_registration') || '邮箱注册'}
          </button>
          <button
            type="button"
            className={`flex-1 py-2 text-center font-medium transition ${registrationMode === 'phone'
              ? 'text-coral border-b-2 border-coral'
              : 'text-gray-500 hover:text-gray-700'
              }`}
            onClick={() => {
              setRegistrationMode('phone');
              setError(null);
            }}
          >
            {t('auth.phone_registration') || '手机注册'}
          </button>
        </div>

        {/* 登録フォーム本体 */}
        <form onSubmit={handleRegister} className="space-y-4" autoComplete="on">
          {registrationMode === 'email' ? (
            <>
              {/* メールアドレス入力欄 */}
              <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
                <FaEnvelope className="text-gray-400 mr-2" />
                <input
                  name="email"
                  type="email"
                  placeholder={t('auth.email') || 'メールアドレス'}
                  className="w-full outline-none text-gray-700"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  aria-required="true"
                />
              </div>
            </>
          ) : (
            <>
              {/* 電話番号（国番号 +81 固定） */}
              <div>
                <div className="flex gap-2">
                  {/* 国番号セレクト（現在は +81 固定） */}
                  <div className="w-24 flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-gray-50">
                    <select
                      className="w-full outline-none bg-transparent text-gray-700 font-medium"
                      value="+81"
                      disabled
                    >
                      <option value="+81">🇯🇵 +81</option>
                    </select>
                  </div>

                  {/* Phone Number (Split Fields 3-4-4) */}
                  <div className="flex-1 flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition bg-white">
                    <FaPhone className="text-gray-400 mr-2" />
                    <div className="flex items-center w-full gap-1">
                      <input
                        name="phone1"
                        ref={p1Ref}
                        type="tel"
                        placeholder="090"
                        className="w-full outline-none text-gray-700 text-center"
                        value={phone1}
                        onChange={(e) => handlePhoneChange(e, setPhone1, 3, p2Ref)}
                        onPaste={handlePhonePaste}
                        maxLength={3}
                        autoComplete="tel-area-code"
                        required
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        name="phone2"
                        ref={p2Ref}
                        type="tel"
                        placeholder="1234"
                        className="w-full outline-none text-gray-700 text-center"
                        value={phone2}
                        onChange={(e) => handlePhoneChange(e, setPhone2, 4, p3Ref)}
                        maxLength={4}
                        autoComplete="tel-local-prefix"
                        required
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        name="phone3"
                        ref={p3Ref}
                        type="tel"
                        placeholder="5678"
                        className="w-full outline-none text-gray-700 text-center"
                        value={phone3}
                        onChange={(e) => handlePhoneChange(e, setPhone3, 4, null)}
                        maxLength={4}
                        autoComplete="tel-local-suffix"
                        required
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('auth.phone_hint') || '日本の携帯電話番号を入力してください（例：090-1234-5678）'}
                </p>
              </div>

              {/* SMS 認証コード入力欄 + 送信ボタン */}
              <div>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
                    <FaLockIcon className="text-gray-400 mr-2" />
                    <input
                      name="verificationCode"
                      type="text"
                      placeholder={t('auth.verification_code') || '验证码'}
                      className="w-full outline-none text-gray-700"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={sendingCode || countdown > 0}
                    className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {sendingCode
                      ? t('auth.sending_code') || '发送中...'
                      : countdown > 0
                        ? t('auth.resend_in', { seconds: countdown }) || `${countdown}s`
                        : t('auth.send_code') || '发送验证码'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* パスワード入力 & 強度表示 */}
          <div>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
              <FaLock className="text-gray-400 mr-2" />
              <input
                name="password"
                type="password"
                placeholder={t('auth.password') || 'パスワード'}
                className="w-full outline-none text-gray-700"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                required
                aria-required="true"
                minLength={8}
              />
            </div>

            {/* パスワード強度メーター */}
            <div className="mt-2">
              <div className="w-full h-2 bg-gray-200 rounded">
                <div
                  className={`h-2 rounded transition-all duration-300 ${getStrengthColor()}`}
                  style={{ width: `${(strengthLevel / 5) * 100}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {(t('auth.strength.label') || '強度') + ': '}
                <strong>{getStrengthLabel()}</strong>
              </p>
            </div>

            {/* パスワード条件の一覧 */}
            <div className="mt-3 space-y-1">
              {renderRule(rules.length, t('auth.password_rule.length') || '8文字以上')}
              {renderRule(rules.uppercase, t('auth.password_rule.uppercase') || '英大文字を含む')}
              {renderRule(rules.lowercase, t('auth.password_rule.lowercase') || '英小文字を含む')}
              {renderRule(rules.number, t('auth.password_rule.number') || '数字を含む')}
              {renderRule(rules.special, t('auth.password_rule.special') || '記号を含む')}
            </div>
          </div>

          {/* Confirm Password - Added for consistency */}
          <div>
            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-coral transition">
              <FaLock className="text-gray-400 mr-2" />
              <input
                name="confirmPassword"
                type="password"
                placeholder={t('auth.confirm_password') || 'パスワード（確認）'}
                className="w-full outline-none text-gray-700"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </div>


          {/* クーポン入力欄（リンク経由の場合はロック） */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              {t('auth.coupon_label') || 'クーポン（任意）'}
            </label>
            <div
              className={`flex items-center rounded-lg px-3 py-2 border transition ${couponLocked
                ? 'bg-gray-50 border-gray-300'
                : 'border-gray-300 focus-within:ring-2 focus-within:ring-coral'
                }`}
            >
              <FaTag className="text-gray-400 mr-2" />
              <input
                name="coupon"
                type="text"
                placeholder={t('auth.coupon_placeholder') || '例）COMP-7K9X2F'}
                className={`w-full outline-none text-gray-700 ${couponLocked ? 'cursor-not-allowed' : ''
                  }`}
                value={coupon}
                onChange={(e) => !couponLocked && handleCouponChange(e.target.value)}
                disabled={couponLocked}
                inputMode="text"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              {couponLocked && (
                <FaLockIcon
                  className="text-gray-400 ml-2"
                  title={t('auth.coupon_locked_hint') || 'リンクから適用済み'}
                />
              )}
            </div>
            {!couponLocked && couponError && (
              <p className="text-xs text-red-600 mt-1">{couponError}</p>
            )}
            {couponLocked && (
              <p className="text-xs text-gray-500 mt-1">
                {t('auth.coupon_locked_hint') || 'リンクから適用済みのため編集できません'}
              </p>
            )}
          </div>

          {/* メール / 電話登録の「登録する」ボタン */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-coral text-white py-2 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-60 ${error && shakeBtn ? 'animate-shake' : ''
              }`}
            aria-busy={loading}
            aria-live="polite"
          >
            {loading
              ? t('auth.registering') || t('auth.register_button') || '登録中…'
              : t('auth.register_button') || '登録'}
          </button>
        </form>

        {/* WeChat ログインボタン（メール登録とは別の認証ルート） */}
        <div className="mt-4">
          <WeChatLoginButton />
        </div>

        {/* 既にアカウントを持っている場合のログイン誘導 */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>{t('auth.have_account') || '既にアカウントをお持ちの方'}</p>
          <button
            onClick={() => navigate(`/login?redirectTo=${encodeURIComponent(redirectTo)}`)}
            className="mt-2 text-coral hover:underline font-medium"
          >
            {t('auth.go_login') || 'ログインへ'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Register;
